// @flow

import { ActionsObservable, combineEpics } from 'redux-observable';
import { concat, from, of, pipe, type Observable } from 'rxjs';
import { distinct, filter, map, switchMap, takeUntil } from 'rxjs/operators';

import footballFireworksImage from '../../assets/images/football-fireworks.png';
import config from '../../config/app.config.js';
import { __ } from '../../i18n/l10n';
import { results } from '../../MockUp';
import { ErrorCodes } from '../../types/errors.js';
import { API } from '../../utils/APIUtils';
import { generateId } from '../../utils/CommonUtils';
import {
  CLEAR_FC_DATA,
  GET_CAPTCHA,
  GET_SESSION_ID,
  globalLoading,
  GLOBAL_LOADING,
  SAVE_EMAIL,
  SAVE_IDENTITY_SCAN,
  SAVE_JWT,
  SAVE_PHONE,
  SET_SESSION_ID,
  WS_CLEAR_PENDING,
  WS_CLEAR_RESPONSES,
  WS_CREATE_USER_REGISTRATION_ERROR,
  WS_CREATE_USER_REGISTRATION_REQUEST,
  WS_CREATE_USER_REGISTRATION_SUCCESS,
  WS_DISPLAY_ERROR_LAYER,
  WS_GET_PLACE_DETAILS_ERROR,
  WS_GET_PLACE_DETAILS_REQUEST,
  WS_GET_PLACE_DETAILS_SUCCESS,
  WS_GET_RECAPTCHA,
  WS_GET_RECAPTCHA_SUCCESS,
  WS_GET_USER_REGISTRATION_ERROR,
  WS_GET_USER_REGISTRATION_REQUEST,
  WS_GET_USER_REGISTRATION_SUCCESS,
  WS_INIT_REGISTRATION,
  WS_INIT_REGISTRATION_SUCCESS,
  WS_IS_AVAILABLE_NICKNAME_ERROR,
  WS_IS_AVAILABLE_NICKNAME_REQUEST,
  WS_IS_AVAILABLE_NICKNAME_SUCCESS,
  WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_ERROR,
  WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_REQUEST,
  WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_SUCCESS,
  WS_SCAN_USER_REGISTRATION_KYC_ERROR,
  WS_SCAN_USER_REGISTRATION_KYC_REQUEST,
  WS_SCAN_USER_REGISTRATION_KYC_SUCCESS,
  WS_SEARCH_PLACES_ERROR,
  WS_SEARCH_PLACES_REQUEST,
  WS_SEARCH_PLACES_SUCCESS,
  WS_SEND_USER_REGISTRATION_SMS_CODE_ERROR,
  WS_SEND_USER_REGISTRATION_SMS_CODE_REQUEST,
  WS_SEND_USER_REGISTRATION_SMS_CODE_SUCCESS,
  WS_SEND_USER_REGISTRATION_VALIDATION_CODE_ERROR,
  WS_SEND_USER_REGISTRATION_VALIDATION_CODE_REQUEST,
  WS_SEND_USER_REGISTRATION_VALIDATION_CODE_SUCCESS,
  WS_SUGGEST_NICKNAME_ERROR,
  WS_SUGGEST_NICKNAME_REQUEST,
  WS_SUGGEST_NICKNAME_SUCCESS,
  WS_UPDATE_USER_REGISTRATION_ERROR,
  WS_UPDATE_USER_REGISTRATION_REQUEST,
  WS_UPDATE_USER_REGISTRATION_SUCCESS,
  WS_UPLOAD_USER_REGISTRATION_KYC_ERROR,
  WS_UPLOAD_USER_REGISTRATION_KYC_REQUEST,
  WS_UPLOAD_USER_REGISTRATION_KYC_SUCCESS,
  WS_VALIDATE_USER_REGISTRATION_DOMICILIATION_CODE_ERROR,
  WS_VALIDATE_USER_REGISTRATION_DOMICILIATION_CODE_REQUEST,
  WS_VALIDATE_USER_REGISTRATION_DOMICILIATION_CODE_SUCCESS,
  WS_VALIDATE_USER_REGISTRATION_SMS_CODE_ERROR,
  WS_VALIDATE_USER_REGISTRATION_SMS_CODE_REQUEST,
  WS_VALIDATE_USER_REGISTRATION_SMS_CODE_SUCCESS,
  WS_GET_LANDING_SUCCESS,
} from '../actions';
import type {
  ApiRequest,
  InitUserRegistrationParams,
  RootState,
  StateObservable,
  UserRegistrationActions,
} from '../types';

import { initConnect, sendMessage } from './websocketEpics';

const afterCaptcha = (state$: StateObservable<RootState>, codeToExecute: any) =>
  pipe(
    filter(action => action.type === 'WS_INIT_REGISTRATION'),
    switchMap((action: UserRegistrationActions) => {
      if (config.shouldUseHcaptcha) {
        const toPipe = [];
        const { captcha } = state$.value.userRegistration;

        if (!captcha.fetching && captcha.token === '') {
          toPipe.push(of({ type: GET_CAPTCHA }));
        }

        toPipe.push(
          state$.pipe(
            filter(state => state.userRegistration.captcha.token !== ''),
            distinct(state => state.userRegistration.captcha.token !== ''),
            initConnect(state$, () => codeToExecute(action)),
          ),
        );

        return concat(...toPipe);
      }

      if (state$.value.userRegistration.recaptcha !== '') {
        return codeToExecute(action);
      }

      const toPipe = [];
      if (
        !state$.value.userRegistration.recaptching
        || (state$.value.userRegistration.recaptching
          && state$.value.userRegistration.recaptcha === '')
      ) {
        toPipe.push(of({ type: WS_GET_RECAPTCHA }));
      }
      toPipe.push(
        state$.pipe(
          filter(state => state.userRegistration.recaptcha !== ''),
          distinct(state => state.userRegistration.recaptcha !== ''),
          switchMap(() => {
            return codeToExecute(action);
          }),
        ),
      );

      return concat(...toPipe);
    }),
  );

export const afterInitRegistration = (
  state$: StateObservable<RootState>,
  codeToExecute: any,
): rxjs$UnaryFunction<Observable<any>, Observable<any>> => {
  return pipe(
    switchMap((action: UserRegistrationActions) => {
      if (state$.value.userRegistration.initialized) {
        return codeToExecute(action);
      }

      const toPipe = [];
      if (
        !state$.value.userRegistration.initializing
        || (state$.value.userRegistration.initializing && !state$.value.userRegistration.initialized)
      ) {
        toPipe.push(of({ type: WS_INIT_REGISTRATION }));
      }
      toPipe.push(
        state$.pipe(
          filter(state => state.userRegistration.initialized),
          distinct(state => state.userRegistration.initialized),
          switchMap(() => {
            return codeToExecute(action);
          }),
        ),
      );

      return concat(...toPipe);
    }),
  );
};

export const initUserRegistrationEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(action => action.type === WS_INIT_REGISTRATION),
    afterCaptcha(state$, (actionSend: UserRegistrationActions) => {
      const json: ApiRequest<InitUserRegistrationParams> = {
        method: 'InitUserRegistration',
        params: {
          lang: state$.value.i18n.language,
          license: config.license,
          tracker: config.tracker,
        },
        id: generateId('InitUserRegistration'),
      };

      if (config.shouldUseHcaptcha) {
        json.params.hcaptcha_token = state$.value.userRegistration.captcha.token;
      } else {
        json.params.recaptcha_token = !config.hosted
          ? 'GO0DT0K3N'
          : state$.value.userRegistration.recaptcha;
      }

      return sendMessage<
        InitUserRegistrationParams,
      InitUserRegistrationResponse,
      InitUserRegistrationError
      >(json).pipe(
        map((payload) => {
          if (config.isMockUp) payload = results.InitUserRegistration;
          if (payload.error) {
            if (payload.error.code === 1407) {
              return {
                type: WS_DISPLAY_ERROR_LAYER,
                payload: {
                  ...payload.error,
                  // __('You cannot create an account from your country.')
                  message: 'You cannot create an account from your country.',
                },
              };
            }

            return {
              type: WS_DISPLAY_ERROR_LAYER,
              payload: {
                ...payload.error,
                retryAction: actionSend,
                codeMessage: 'CAPTCHA',
                // __('CAPTCHA score error. Please try again. You will automatically be redirected to the Winamax website. (%1$s)')
                message:
                  'CAPTCHA score error. Please try again. You will automatically be redirected to the Winamax website. (%1$s)',
              },
            };
          }
          return {
            type: WS_INIT_REGISTRATION_SUCCESS,
            payload,
          };
        }),
        takeUntil(action$.ofType(WS_INIT_REGISTRATION_SUCCESS)),
      );
    }),
  );

export const createUserRegistrationRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(action => action.type === WS_CREATE_USER_REGISTRATION_REQUEST && !config.isMockUp),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_CREATE_USER_REGISTRATION_REQUEST) {
        const dataLayer = state$.value.tagManager?.dataLayer;

        const creationAffiliateName
          = dataLayer?.visit_affiliate !== '' ? dataLayer?.visit_affiliate : undefined;
        const creationBannerName
          = dataLayer?.visit_info1 !== '' ? dataLayer?.visit_info1 : undefined;

        return sendMessage<
          CreateUserRegistrationParams,
        CreateUserRegistrationResponse,
        CreateUserRegistrationError
        >({
          ...actionSend.payload,
          params: {
            ...actionSend.payload.params,
            creation_affiliate_name: creationAffiliateName,
            creation_banner_name: creationBannerName,
            lang: state$.value.i18n.language,
          },
          method: API.CreateUserRegistration,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_CREATE_USER_REGISTRATION_ERROR,
                  payload: payload.error,
                },
                { type: WS_CLEAR_PENDING },
              ];

              if (payload.error.code === -30409) {
                dispatchActions.push({
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: {
                    codeMessage: 'USER_ALREADY_EXISTS',
                    message: payload.error.message,
                  },
                });
                return of(...dispatchActions);
              }

              if (
                !payload.error.data
                || !payload.error.data.field_violations
                || payload.error.data.field_violations[0].field === 'method'
              ) {
                dispatchActions.push({
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                });
              }

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_CREATE_USER_REGISTRATION_SUCCESS,
                payload: payload.result,
              },
              { type: SAVE_EMAIL, payload: actionSend.payload.params.email },
              {
                type: SAVE_PHONE,
                payload: actionSend.payload.params.international_phone_number,
              },
            ];

            if (payload.result && payload.result.jwt) {
              const { jwt } = payload.result;

              dispatchActions.push({
                type: SAVE_JWT,
                payload: jwt,
              });

              dispatchActions.push({
                type: SET_SESSION_ID,
              });

              if (!state$.value.userRegistration.user) {
                dispatchActions.push({
                  type: WS_GET_USER_REGISTRATION_REQUEST,
                  payload: {
                    method: API.GetUserRegistration,
                    params: { jwt },
                    backgroundAPI: true,
                  },
                });
              }
            }

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const getUserRegistrationRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(action => action.type === WS_GET_USER_REGISTRATION_REQUEST && !config.isMockUp),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_GET_USER_REGISTRATION_REQUEST) {
        return sendMessage<
          GetUserRegistrationParams,
        GetUserRegistrationResponse,
        GetUserRegistrationError
        >({
          ...actionSend.payload,
          method: API.GetUserRegistration,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              return of({
                type: WS_GET_USER_REGISTRATION_ERROR,
                payload: payload.error,
              });
            }

            const dispatchActions = [
              {
                type: WS_GET_USER_REGISTRATION_SUCCESS,
                payload: payload.result,
              },
              {
                type: GLOBAL_LOADING,
                payload: false,
              },
              {
                type: CLEAR_FC_DATA,
                payload: false,
              },
            ];

            if (payload.result.landing !== null) {
              dispatchActions.push({
                type: WS_GET_LANDING_SUCCESS,
                payload: { data: { name: payload.result.landing } },
              });
            }

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const sendUserRegistrationSmsCodeRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(
      action => action.type === WS_SEND_USER_REGISTRATION_SMS_CODE_REQUEST && !config.isMockUp,
    ),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_SEND_USER_REGISTRATION_SMS_CODE_REQUEST) {
        return sendMessage<
          SendUserRegistrationSmsCodeParams,
        SendUserRegistrationSmsCodeResponse,
        SendUserRegistrationSmsCodeError
        >({
          ...actionSend.payload,
          params: { ...actionSend.payload.params, language: state$.value.i18n.language },
          method: API.SendUserRegistrationSMSCode,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                { type: WS_CLEAR_PENDING },
                {
                  type: WS_SEND_USER_REGISTRATION_SMS_CODE_ERROR,
                  payload: payload.error,
                },
              ];

              if (payload.error.code === -30409) {
                dispatchActions.push({
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: {
                    codeMessage: 'USER_ALREADY_EXISTS',
                    message: payload.error.message,
                  },
                });
                return of(...dispatchActions);
              }

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_SEND_USER_REGISTRATION_SMS_CODE_SUCCESS,
                payload: payload.result,
              },
              { type: WS_CLEAR_PENDING },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const validateUserRegistrationSmsCodeRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(
      action => action.type === WS_VALIDATE_USER_REGISTRATION_SMS_CODE_REQUEST && !config.isMockUp,
    ),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_VALIDATE_USER_REGISTRATION_SMS_CODE_REQUEST) {
        return sendMessage<
          ValidateUserRegistrationSmsCodeParams,
        ValidateUserRegistrationSmsCodeResponse,
        ValidateUserRegistrationSmsCodeError
        >({
          ...actionSend.payload,
          method: API.ValidateUserRegistrationSMSCode,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_VALIDATE_USER_REGISTRATION_SMS_CODE_ERROR,
                  payload: payload.error,
                },
                { type: WS_CLEAR_PENDING },
              ];

              if (
                !payload.error.data
                || !payload.error.data.field_violations
                || payload.error.data.field_violations[0].field === 'method'
              ) {
                dispatchActions.push({
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                });
              }

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_VALIDATE_USER_REGISTRATION_SMS_CODE_SUCCESS,
                payload: payload.result,
              },
              { type: WS_CLEAR_PENDING },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const updateUserRegistrationRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(action => action.type === WS_UPDATE_USER_REGISTRATION_REQUEST && !config.isMockUp),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_UPDATE_USER_REGISTRATION_REQUEST) {
        return sendMessage<
          UpdateUserRegistrationParams,
        UpdateUserRegistrationResponse,
        UpdateUserRegistrationError
        >({
          ...actionSend.payload,
          method: API.UpdateUserRegistration,
          // $FlowIssue
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [];
              let shouldClearPending = true;

              if (payload.error.message === 'Already exists') {
                if (!payload.error.data) {
                  payload.error.data = {};
                }
                if (!payload.error.data.field_violations) {
                  payload.error.data.field_violations = [];
                }
                payload.error.data.field_violations.push({
                  field: 'email',
                  error: 'Already exist',
                });
              } else if (payload.error.message === 'Invalid params') {
                if (payload.error.data && payload.error.data.field_violations) {
                  const isSessionIdInvalid = payload.error.data.field_violations.find(
                    e => e.field === 'session_id',
                  );

                  if (isSessionIdInvalid) {
                    dispatchActions.push({
                      type: GET_SESSION_ID,
                      dispatchOnSuccess: [actionSend],
                    });
                    shouldClearPending = false;
                  }
                }
              }

              dispatchActions.push({
                type: WS_UPDATE_USER_REGISTRATION_ERROR,
                payload: payload.error,
              });

              if (shouldClearPending) {
                dispatchActions.push({ type: WS_CLEAR_PENDING });
              }

              if (
                payload.error.code === ErrorCodes.E_RESTRICTED_LIMIT
                && payload.error.message
                && payload.error.data?.values
              ) {
                dispatchActions.push({
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: {
                    codeMessage: 'ERROR_VALUES',
                    message: payload.error.message,
                    values: payload.error.data.values,
                  },
                });
              } else if (
                !payload.error.data
                || !payload.error.data.field_violations
                || payload.error.data.field_violations[0].field === 'method'
              ) {
                dispatchActions.push({
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                });
              }

              return of(...dispatchActions);
            }

            const payloadJwt
              // $FlowFixMe
              = payload && payload.result && payload.result.data && payload.result.data.jwt;
            const dispatchActions = [
              {
                type: WS_UPDATE_USER_REGISTRATION_SUCCESS,
                payload: payload.result,
              },
              {
                type: WS_GET_USER_REGISTRATION_REQUEST,
                payload: {
                  method: API.GetUserRegistration,
                  params: { jwt: payloadJwt || state$.value.common.jwt || '' },
                  backgroundAPI: true,
                },
              },
              { type: WS_CLEAR_PENDING },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const uploadUserRegistrationKYCRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(action => action.type === WS_UPLOAD_USER_REGISTRATION_KYC_REQUEST && !config.isMockUp),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_UPLOAD_USER_REGISTRATION_KYC_REQUEST) {
        return sendMessage<
          UploadUserRegistrationKYCParams,
        UploadUserRegistrationKYCResponse,
        UploadUserRegistrationKYCError
        >({
          ...actionSend.payload,
          method: API.UploadUserRegistrationKYC,
          // $FlowFixMe
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_UPLOAD_USER_REGISTRATION_KYC_ERROR,
                  payload: payload.error,
                },
                {
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                },
                { type: WS_CLEAR_PENDING },
                {
                  type: GLOBAL_LOADING,
                  payload: false,
                },
              ];

              return of(...dispatchActions);
            }
            const dispatchActions = [
              {
                type: WS_UPLOAD_USER_REGISTRATION_KYC_SUCCESS,
                payload: payload.result,
              },
            ];

            const needBackKyc
              = state$.value.userRegistration.scanUserRegistrationKYC.response?.needBack;

            // $FlowIssue
            if (payload.result?.data?.type !== 'residence' && !needBackKyc) {
              dispatchActions.push({
                type: WS_SCAN_USER_REGISTRATION_KYC_REQUEST,
                payload: {
                  method: API.ScanUserRegistrationKYC,
                  params: {
                    jwt: actionSend.payload.params.jwt,
                    kyc_id: payload.result.data.kyc_id,
                  },
                },
              });
            }

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const scanUserRegistrationKYCRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(action => action.type === WS_SCAN_USER_REGISTRATION_KYC_REQUEST && !config.isMockUp),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_SCAN_USER_REGISTRATION_KYC_REQUEST) {
        return sendMessage<
          ScanUserRegistrationKYCParams,
        ScanUserRegistrationKYCResponse,
        ScanUserRegistrationKYCError
        >({
          ...actionSend.payload,
          method: API.ScanUserRegistrationKYC,
          // $FlowFixMe
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_SCAN_USER_REGISTRATION_KYC_ERROR,
                  payload: payload.error,
                },
                {
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                },
                { type: WS_CLEAR_PENDING },
                {
                  type: GLOBAL_LOADING,
                  payload: false,
                },
              ];

              return of(...dispatchActions);
            }

            const { data } = payload.result;
            const dispatchActions: object[] = [
              { type: WS_CLEAR_RESPONSES },
              {
                type: WS_SCAN_USER_REGISTRATION_KYC_SUCCESS,
                payload: payload.result,
              },
              { type: WS_CLEAR_PENDING },
              {
                type: SAVE_IDENTITY_SCAN,
                payload: data,
              },
              globalLoading({
                spine: '',
                fallback: footballFireworksImage,
                title: __('Boom!'),
                text: __('Super-player created!'),
              }),
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const suggestNicknameRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(action => action.type === WS_SUGGEST_NICKNAME_REQUEST && !config.isMockUp),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_SUGGEST_NICKNAME_REQUEST) {
        return sendMessage<SuggestNicknameParams, SuggestNicknameResponse, SuggestNicknameError>({
          ...actionSend.payload,
          method: API.SuggestNickname,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_SUGGEST_NICKNAME_ERROR,
                  payload: payload.error,
                },
                {
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                },
                { type: WS_CLEAR_PENDING },
                {
                  type: GLOBAL_LOADING,
                  payload: false,
                },
              ];

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_SUGGEST_NICKNAME_SUCCESS,
                payload: payload.result,
              },
              { type: WS_CLEAR_PENDING },
              {
                type: GLOBAL_LOADING,
                payload: false,
              },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const isAvailableNicknameRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(action => action.type === WS_IS_AVAILABLE_NICKNAME_REQUEST && !config.isMockUp),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_IS_AVAILABLE_NICKNAME_REQUEST) {
        return sendMessage<
          IsAvailableNicknameParams,
        IsAvailableNicknameResponse,
        IsAvailableNicknameError
        >({
          ...actionSend.payload,
          method: API.IsAvailableNickname,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const { error } = payload;

              // Fix for API returning fieldViolations instead of field_violations
              if (error.data && error.data.fieldViolations) {
                error.data.field_violations = error.data.fieldViolations;
              }

              const dispatchActions = [
                {
                  type: WS_IS_AVAILABLE_NICKNAME_ERROR,
                  payload: error,
                },
                { type: WS_CLEAR_PENDING },
                {
                  type: GLOBAL_LOADING,
                  payload: false,
                },
              ];

              if (
                !error.data
                || !error.data.field_violations
                || error.data.field_violations[0].field === 'method'
              ) {
                dispatchActions.push({
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: error,
                });
              }

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_IS_AVAILABLE_NICKNAME_SUCCESS,
                payload: payload.result,
              },
              { type: WS_CLEAR_PENDING },
              {
                type: GLOBAL_LOADING,
                payload: false,
              },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const validateUserRegistrationDomiciliationCodeRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(
      action =>
        action.type === WS_VALIDATE_USER_REGISTRATION_DOMICILIATION_CODE_REQUEST && !config.isMockUp,
    ),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_VALIDATE_USER_REGISTRATION_DOMICILIATION_CODE_REQUEST) {
        return sendMessage<
          ValidateUserRegistrationDomiciliationCodeParams,
        ValidateUserRegistrationDomiciliationCodeResponse,
        ValidateUserRegistrationDomiciliationCodeError
        >({
          ...actionSend.payload,
          method: API.ValidateUserRegistrationDomiciliationCode,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_VALIDATE_USER_REGISTRATION_DOMICILIATION_CODE_ERROR,
                  payload: payload.error,
                },
                { type: WS_CLEAR_PENDING },
                {
                  type: GLOBAL_LOADING,
                  payload: false,
                },
              ];

              if (
                !payload.error.data
                || !payload.error.data.field_violations
                || payload.error.data.field_violations[0].field === 'method'
              ) {
                dispatchActions.push({
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                });
              }

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_VALIDATE_USER_REGISTRATION_DOMICILIATION_CODE_SUCCESS,
                payload: payload.result,
              },
              { type: WS_CLEAR_PENDING },
              {
                type: GLOBAL_LOADING,
                payload: false,
              },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const sendUserRegistrationValidationCodeRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(
      action =>
        action.type === WS_SEND_USER_REGISTRATION_VALIDATION_CODE_REQUEST && !config.isMockUp,
    ),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_SEND_USER_REGISTRATION_VALIDATION_CODE_REQUEST) {
        return sendMessage<
          SendUserRegistrationValidationCodeParams,
        SendUserRegistrationValidationCodeResponse,
        SendUserRegistrationValidationCodeError
        >({
          ...actionSend.payload,
          method: API.SendUserRegistrationValidationCode,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_SEND_USER_REGISTRATION_VALIDATION_CODE_ERROR,
                  payload: payload.error,
                },
                {
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                },
                { type: WS_CLEAR_PENDING },
                {
                  type: GLOBAL_LOADING,
                  payload: false,
                },
              ];

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_SEND_USER_REGISTRATION_VALIDATION_CODE_SUCCESS,
                payload: payload.result,
              },
              { type: WS_CLEAR_PENDING },
              {
                type: GLOBAL_LOADING,
                payload: false,
              },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const listInternationalCountryCallingCodesRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(
      action =>
        action.type === WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_REQUEST && !config.isMockUp,
    ),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_REQUEST) {
        return sendMessage<
          ListInternationalCountryCallingCodesParams,
        ListInternationalCountryCallingCodesResponse,
        ListInternationalCountryCallingCodesError
        >({
          ...actionSend.payload,
          method: API.ListInternationalCountryCallingCodes,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_ERROR,
                  payload: payload.error,
                },
                {
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                },
              ];

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_SUCCESS,
                payload: payload.result,
              },
              { type: WS_CLEAR_PENDING },
              {
                type: GLOBAL_LOADING,
                payload: false,
              },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const searchPlacesRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(action => action.type === WS_SEARCH_PLACES_REQUEST && !config.isMockUp),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_SEARCH_PLACES_REQUEST) {
        return sendMessage<SearchPlacesParams, SearchPlacesResponse, SearchPlacesError>({
          ...actionSend.payload,
          method: API.SearchPlaces,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_SEARCH_PLACES_ERROR,
                  payload: payload.error,
                },
                {
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                },
              ];

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_SEARCH_PLACES_SUCCESS,
                payload: payload.result,
              },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const getPlaceDetailsRequestEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(action => action.type === WS_GET_PLACE_DETAILS_REQUEST && !config.isMockUp),
    afterInitRegistration(state$, (actionSend: UserRegistrationActions) => {
      if (actionSend.type === WS_GET_PLACE_DETAILS_REQUEST) {
        return sendMessage<GetPlaceDetailsParams, GetPlaceDetailsResponse, GetPlaceDetailsError>({
          ...actionSend.payload,
          method: API.GetPlaceDetails,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_GET_PLACE_DETAILS_ERROR,
                  payload: payload.error,
                },
                {
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                },
              ];

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_GET_PLACE_DETAILS_SUCCESS,
                payload: {
                  // $FlowFixMe
                  originField: actionSend.payload.originField,
                  addressData: payload.result,
                },
              },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const getRecaptchaEpic = (
  action$: ActionsObservable<UserRegistrationActions>,
  state$: any,
) =>
  action$.pipe(
    filter(action => action.type === WS_GET_RECAPTCHA),
    initConnect(state$, () => {
      const { recaptchaExec } = state$.value.userRegistration;
      if (!recaptchaExec) {
        // $FlowFixMe
        return [];
      }
      // $FlowFixMe
      return from(recaptchaExec('main')).pipe(
        map((token: string) => ({
          type: WS_GET_RECAPTCHA_SUCCESS,
          payload: token,
        })),
      );
    }),
  );

export const userRegistrationEpics = combineEpics(
  initUserRegistrationEpic,
  createUserRegistrationRequestEpic,
  getUserRegistrationRequestEpic,
  sendUserRegistrationSmsCodeRequestEpic,
  validateUserRegistrationSmsCodeRequestEpic,
  updateUserRegistrationRequestEpic,
  uploadUserRegistrationKYCRequestEpic,
  scanUserRegistrationKYCRequestEpic,
  suggestNicknameRequestEpic,
  isAvailableNicknameRequestEpic,
  validateUserRegistrationDomiciliationCodeRequestEpic,
  sendUserRegistrationValidationCodeRequestEpic,
  listInternationalCountryCallingCodesRequestEpic,
  searchPlacesRequestEpic,
  getPlaceDetailsRequestEpic,
  getRecaptchaEpic,
);
